import { useEffect, useMemo, useState } from 'react'
import { listDataGovernors, listHubs } from '../../../api/client'
import type { DataGovernor } from '../../../api/client'
import {
  DEFAULT_ENVIRONMENT,
  ENVIRONMENT_TABS,
  PRODUCTION_ENVIRONMENT,
  localText,
  type EnvironmentTab,
} from './DataGovernorsPage.static'

export interface Notice {
  readonly title: string
  readonly message: string
  readonly variant: 'error' | 'info' | 'success'
}

export interface MutationOutcome {
  readonly changed: boolean
  readonly message: string
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : localText.defaultErrorMessage
}

export function useDataGovernorsPage() {
  const [activeTab, setActiveTab] = useState<EnvironmentTab>('prod')
  const [intEnvironment, setIntEnvironment] = useState(DEFAULT_ENVIRONMENT)
  const [refreshKey, setRefreshKey] = useState(0)
  const [governors, setGovernors] = useState<DataGovernor[]>([])
  const [hubFriendlyNames, setHubFriendlyNames] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [searchText, setSearchText] = useState('')
  const [hubFilter, setHubFilter] = useState('')
  const [businessObjectFilter, setBusinessObjectFilter] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [governorToRemove, setGovernorToRemove] = useState<DataGovernor | null>(null)

  const environment = activeTab === 'prod' ? PRODUCTION_ENVIRONMENT : intEnvironment

  useEffect(() => {
    let isCurrent = true

    async function loadGovernors() {
      setIsLoading(true)
      setError(null)

      try {
        const response = await listDataGovernors({ environment })

        if (isCurrent) {
          setGovernors(response.governors)
          setHasLoaded(true)
        }
      } catch (loadError) {
        if (isCurrent) {
          setError(errorMessage(loadError))
          setHasLoaded(true)
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false)
        }
      }
    }

    void loadGovernors()

    return () => {
      isCurrent = false
    }
  }, [environment, refreshKey])

  useEffect(() => {
    let isCurrent = true

    // Best-effort: hub friendly names are a display nicety (see GET /hubs), so a failure here
    // shouldn't block the page -- values just stay as their raw uppercased key (see hubFriendlyName).
    listHubs({ environment })
      .then((response) => {
        if (isCurrent) {
          setHubFriendlyNames(
            Object.fromEntries(response.hubs.map((hub) => [hub.name, hub.friendly_name])),
          )
        }
      })
      .catch(() => {})

    return () => {
      isCurrent = false
    }
  }, [environment])

  // ponytail: there is no hub/business-object catalog endpoint yet (see docs/actions/data-governor.md),
  // so filter and add-dialog options are derived from what the registry already returned for this
  // environment rather than a separate lookup call.
  const hubOptions = useMemo(
    () => Array.from(new Set(governors.map((governor) => governor.hub))).sort(),
    [governors],
  )
  const businessObjectOptions = useMemo(
    () => Array.from(new Set(governors.map((governor) => governor.business_object))).sort(),
    [governors],
  )
  const hubCount = hubOptions.length

  const filteredGovernors = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()

    return governors.filter((governor) => {
      const matchesSearch = !normalizedSearch || governor.email.toLowerCase().includes(normalizedSearch)
      const matchesHub = !hubFilter || governor.hub === hubFilter
      const matchesBusinessObject = !businessObjectFilter || governor.business_object === businessObjectFilter
      return matchesSearch && matchesHub && matchesBusinessObject
    })
  }, [governors, searchText, hubFilter, businessObjectFilter])

  function selectTab(tabIndex: number) {
    const nextTab = ENVIRONMENT_TABS[tabIndex]?.tab ?? 'prod'
    setActiveTab(nextTab)
    setSearchText('')
    setHubFilter('')
    setBusinessObjectFilter('')
  }

  function openAddDialog() {
    setIsAddDialogOpen(true)
  }

  function closeAddDialog() {
    setIsAddDialogOpen(false)
  }

  function closeRemoveDialog() {
    setGovernorToRemove(null)
  }

  function handleGovernorAdded({ changed, message }: MutationOutcome) {
    setNotice({
      title: changed ? localText.governorAddedTitle : localText.noRegistryChangeTitle,
      message,
      variant: changed ? 'success' : 'info',
    })
    setIsAddDialogOpen(false)
    setRefreshKey((current) => current + 1)
  }

  function handleGovernorRemoved({ changed, message }: MutationOutcome) {
    setNotice({
      title: changed ? localText.governorRemovedTitle : localText.noRegistryChangeTitle,
      message,
      variant: changed ? 'success' : 'info',
    })
    setGovernorToRemove(null)
    setRefreshKey((current) => current + 1)
  }

  return {
    activeTab,
    intEnvironment,
    setIntEnvironment,
    environment,
    governors,
    hubFriendlyNames,
    isLoading,
    hasLoaded,
    error,
    setError,
    notice,
    setNotice,
    searchText,
    setSearchText,
    hubFilter,
    setHubFilter,
    businessObjectFilter,
    setBusinessObjectFilter,
    isAddDialogOpen,
    governorToRemove,
    setGovernorToRemove,
    hubOptions,
    businessObjectOptions,
    hubCount,
    filteredGovernors,
    selectTab,
    openAddDialog,
    closeAddDialog,
    closeRemoveDialog,
    handleGovernorAdded,
    handleGovernorRemoved,
  }
}
